/**
 * Institution Case Studies Component
 */

import React from 'react';
import { Grid, Column, Button, Tag } from '@carbon/react';
import { ArrowRight, DocumentPdf } from '@carbon/icons-react';
import type { CaseStudy } from '../../../../shared/interfaces/InstitutionalLanding';

interface InstitutionCaseStudiesProps {
  caseStudies: CaseStudy[];
}

const InstitutionCaseStudies: React.FC<InstitutionCaseStudiesProps> = ({ caseStudies }) => {
  return (
    <section className="institution-case-studies">
      <Grid narrow>
        <Column lg={16}>
          <h2>Success Stories</h2>
          <p className="section-subtitle">Real results from our institution partners</p>
        </Column>
        {caseStudies.map(study => (
          <Column key={study.id} lg={16}>
            <div className="case-study-card">
              <Grid>
                <Column lg={10}>
                  <h3>{study.title}</h3>
                  <p className="study-summary">{study.summary}</p>
                  <div className="study-details">
                    <div className="study-section">
                      <h4>Challenge</h4>
                      <p>{study.challenge}</p>
                    </div>
                    <div className="study-section">
                      <h4>Solution</h4>
                      <p>{study.solution}</p>
                    </div>
                    <div className="study-section">
                      <h4>Results</h4>
                      <ul>
                        {study.results.map((result, i) => (
                          <li key={i}>{result}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  {study.pdfUrl && (
                    <Button
                      kind="tertiary"
                      size="sm"
                      renderIcon={DocumentPdf}
                      onClick={() => window.open(study.pdfUrl, '_blank')}
                    >
                      Download Full Case Study
                    </Button>
                  )}
                </Column>
                <Column lg={6}>
                  {study.metrics && (
                    <div className="study-metrics">
                      {study.metrics.map((metric, i) => (
                        <div key={i} className="metric-card">
                          <div className="metric-value">{metric.value}</div>
                          <div className="metric-label">{metric.label}</div>
                          {metric.improvement && (
                            <Tag type="green" size="sm">
                              {metric.improvement}
                            </Tag>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </Column>
              </Grid>
            </div>
          </Column>
        ))}
      </Grid>
      <style>{`
        .institution-case-studies {
          padding: 4rem 0;
          background-color: var(--cds-background);
        }
        .institution-case-studies h2 {
          font-size: 2.5rem;
          font-weight: 600;
          text-align: center;
          margin-bottom: 1rem;
        }
        .section-subtitle {
          text-align: center;
          color: var(--cds-text-secondary);
          margin-bottom: 3rem;
        }
        .case-study-card {
          background-color: var(--cds-layer-01);
          padding: 2.5rem;
          margin-bottom: 2rem;
        }
        .case-study-card h3 {
          font-size: 1.75rem;
          margin-bottom: 1rem;
          color: var(--cds-interactive-01);
        }
        .study-summary {
          font-size: 1.125rem;
          margin-bottom: 2rem;
          color: var(--cds-text-secondary);
        }
        .study-section {
          margin-bottom: 1.5rem;
        }
        .study-section h4 {
          font-size: 0.875rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          margin-bottom: 0.5rem;
          color: var(--cds-text-secondary);
        }
        .study-section ul {
          list-style: none;
          padding: 0;
        }
        .study-section li {
          padding-left: 1.5rem;
          position: relative;
          margin-bottom: 0.5rem;
        }
        .study-section li::before {
          content: '→';
          position: absolute;
          left: 0;
          color: var(--cds-interactive-01);
        }
        .study-metrics {
          display: grid;
          gap: 1rem;
        }
        .metric-card {
          background-color: var(--cds-background);
          padding: 1.5rem;
          text-align: center;
        }
        .metric-value {
          font-size: 2rem;
          font-weight: 700;
          color: var(--cds-interactive-01);
          margin-bottom: 0.5rem;
        }
        .metric-label {
          font-size: 0.875rem;
          color: var(--cds-text-secondary);
          margin-bottom: 0.5rem;
        }
      `}</style>
    </section>
  );
};

export default InstitutionCaseStudies;
